import { useQuery } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Printer } from 'lucide-react';
import { appointmentApi } from '@/api/appointment';
import type { Appointment } from '@/types/types';

function formatDateTimeNumeric(date: Date): string {
    // Match OPD card style: DD-MM-YYYY hh:mm AM/PM
    const dt = date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
    // en-GB gives "10/04/2026, 12:36 pm" → "10-04-2026 12:36 pm"
    return dt.replace(',', '').split('/').join('-');
}

function formatDate(date: Date): string {
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function getAge(dob: Date | string | null | undefined): string {
    if (dob == null || dob === "") return '—';
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return '—';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 0 ? `${age}` : '—';
}

function formatSex(gender: string | null | undefined): string {
    if (!gender) return '—';
    const g = String(gender).toUpperCase();
    if (g === 'MALE') return 'M';
    if (g === 'FEMALE') return 'F';
    return gender;
}

function maskMobile(raw: unknown): string {
    const d = String(raw ?? '').replace(/\D/g, '');
    if (!d) return '—';
    if (d.length <= 4) return '****';
    const last4 = d.slice(-4);
    return `${'*'.repeat(Math.max(0, d.length - 4))}${last4}`;
}

/** Prefer UHID for display; fallback to patientUniqueId. Never show raw internal id. */
function formatPatientId(patient: { uhid?: string | null; patientUniqueId?: string; id?: string } | undefined): string {
    if (!patient) return '—';
    if (patient.uhid && String(patient.uhid).trim()) return String(patient.uhid).trim();
    if (patient.patientUniqueId && String(patient.patientUniqueId).trim()) return String(patient.patientUniqueId).trim();
    return '—';
}

interface BlankPrescriptionPrintProps {
    isOpen: boolean;
    onClose: () => void;
    appointmentId: string | null;
    /** When opening from queue, pass the appointment so Print is enabled without waiting for refetch */
    initialAppointment?: Appointment | null;
    /** OPD queue sequence number (from Queue Management) */
    sequenceNo?: number | null;
}

export default function BlankPrescriptionPrint({
    isOpen,
    onClose,
    appointmentId,
    initialAppointment = null,
    sequenceNo = null,
}: BlankPrescriptionPrintProps) {
    const { data: res, isLoading } = useQuery({
        queryKey: ['appointment', appointmentId],
        queryFn: () => appointmentApi.getAppointmentById(appointmentId!),
        enabled: isOpen && !!appointmentId,
    });

    const fetchedAppointment = res?.data?.data as Appointment | undefined;
    // Prefer the fully-fetched appointment (includes full patient details like DOB, gender, UHID).
    const appointment = fetchedAppointment ?? initialAppointment ?? undefined;
    const patient = appointment?.patient;
    const doctor = appointment?.doctor;

    const buildPrintHtml = (): string => {
        const visitAt = appointment?.scheduledAt ? new Date(appointment.scheduledAt as any) : new Date();
        const validUpto = addDays(visitAt, 3);
        const patientId = formatPatientId(patient);
        const patientName = patient?.name ?? '—';
        const age = getAge(patient?.dob);
        const sex = formatSex(patient?.gender);
        const mobileMasked = maskMobile((patient as any)?.phone ?? (patient as any)?.phoneNumber);
        const doctorName = (doctor as any)?.name ?? '—';
        const specialisation = (doctor as any)?.specialisation ?? '—';
        const seqNoText = typeof sequenceNo === 'number' && sequenceNo > 0 ? String(sequenceNo) : '';

        return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>OPD Card</title>
  <style>
    @page { margin: 0.5in; }
    body { font-family: Arial, system-ui, sans-serif; font-size: 12px; color: #111; margin: 0; }
    /* Letterhead reserve + extra 2cm so form content sits lower on the page */
    .header-space { height: calc(64px + 2cm); }
    .footer-space { height: 56px; }
    .line { border-top: 1px solid #cbd5e1; margin: 10px 0; }
    .top { display: flex; justify-content: space-between; gap: 16px; }
    .left { flex: 1.3; }
    .right { flex: 1; text-align: right; }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
    .muted { color: #475569; }
    .label { color: #334155; }
    .kv { line-height: 1.6; }
    .titleRow { display:flex; align-items:center; justify-content:center; font-weight: 800; font-size: 14px; letter-spacing: 0.6px; }
    .small { font-size: 11px; }
    .grid2 { display:grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .section { margin-top: 10px; }
    .boxRow { display:flex; justify-content: space-between; gap: 18px; }
    .box { flex: 1; }
    .row2 { display:flex; gap: 18px; align-items: flex-start; }
    .row2 .col { flex: 1; }
    .row2 .col.narrow { flex: 0 0 360px; }
    .checklist { display:grid; grid-template-columns: 0.9fr 0.9fr; gap: 3px 12px; margin-top: 4px; font-size: 10px; max-width: 360px; }
    .check { display:flex; align-items:center; gap: 6px; }
    .sq { width: 10px; height: 10px; border: 1px solid #111; display:inline-block; }
    .blankLine { display:inline-block; min-width: 140px; border-bottom: 1px solid #111; height: 10px; }
    .blankLineSm { display:inline-block; min-width: 90px; border-bottom: 1px solid #111; height: 10px; }
    .writeArea { height: 90px; }
  </style>
</head>
<body>
  <div class="header-space"></div>

  <div class="top">
    <div class="left kv">
      <div><span class="label">UHID No.</span> <span class="mono">${patientId}</span></div>
      <div><span class="label">Patient Name</span> <span>${patientName}</span></div>
      <div><span class="label">Age / Sex</span> <span>${age} / ${sex}</span></div>
      <div><span class="label">Consultant</span> <span>Dr. ${doctorName}</span></div>
      <div><span class="label">Specialisation</span> <span>${specialisation}</span></div>
      <div><span class="label">Provisional Diagnosis</span> <span class="blankLine" aria-hidden="true"></span></div>
    </div>

    <div class="right kv">
      <div class="titleRow">OPD Card</div>
      <div><span class="label">Seq.No</span> <span class="mono">${seqNoText || ''}</span>${seqNoText ? '' : ' <span class="blankLineSm" aria-hidden="true"></span>'}</div>
      <div><span class="label">Visit Date</span> <span class="mono">${formatDateTimeNumeric(visitAt)}</span></div>
      <div><span class="label">Mobile No.</span> <span class="mono">${mobileMasked}</span></div>
      <div><span class="label">Valid Up to</span> <span class="mono">${formatDate(validUpto)}</span></div>
    </div>
  </div>

  <div class="line"></div>

  <div class="section">
    <div class="row2">
      <div class="col narrow">
        <div class="label" style="font-weight:600; font-size: 11px;">Past History</div>
        <div class="checklist" style="margin-left: 0;">
          <div class="check"><span class="sq"></span><span>Previous Major Hospitalization</span></div>
          <div class="check"><span class="sq"></span><span>Previous drug History</span></div>
          <div class="check"><span class="sq"></span><span>Surgery</span></div>
          <div class="check"><span class="sq"></span><span>Allergies</span></div>
          <div class="check"><span class="sq"></span><span>Thyroid</span></div>
          <div class="check"><span class="sq"></span><span>Epilepsy</span></div>
          <div class="check"><span class="sq"></span><span>Hypertension</span></div>
          <div class="check"><span class="sq"></span><span>Diabetes</span></div>
        </div>
      </div>
      <div class="col small">
        <div class="label" style="font-weight:600;">VITALS</div>
        <div style="margin-top:6px; display:flex; gap:18px; flex-wrap:wrap;">
          <div><span class="label">Temp</span> <span class="blankLineSm" aria-hidden="true"></span></div>
          <div><span class="label">BP</span> <span class="blankLineSm" aria-hidden="true"></span></div>
          <div><span class="label">R/R</span> <span class="blankLineSm" aria-hidden="true"></span></div>
          <div><span class="label">Pulse</span> <span class="blankLineSm" aria-hidden="true"></span></div>
          <div><span class="label">SpO2</span> <span class="blankLineSm" aria-hidden="true"></span></div>
        </div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="label" style="font-weight:600;">Chief Complaints</div>
    <div class="writeArea" style="margin-top:6px; height: 110px;"></div>
  </div>

  <div class="section" style="margin-top:14px;">
    <div class="label" style="font-weight:600;">Treatment / Prescription</div>
    <div class="writeArea" style="margin-top:6px; height: 140px;"></div>
  </div>

  <div class="section" style="margin-top:14px;">
    <div class="label" style="font-weight:600;">Investigation Adviced</div>
    <div class="writeArea" style="margin-top:6px; height: 110px;"></div>
    </div>

  <div class="section" style="margin-top:18px;">
    <div class="label" style="font-weight:600;">Follow up</div>
    <div class="blankLine" style="min-width: 220px;" aria-hidden="true"></div>
  </div>

  <div class="footer-space"></div>
  <script>
    window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };
  </script>
</body>
</html>`;
    };

    const handlePrint = () => {
        const html = buildPrintHtml();
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow pop-ups to print the prescription.');
            return;
        }
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const visitAt = appointment?.scheduledAt ? new Date(appointment.scheduledAt as any) : new Date();
    const validUpto = addDays(visitAt, 3);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Print Blank Prescription</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {isLoading && !appointment && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                    )}
                    {appointment && (
                        <div className="bg-white text-gray-900">
                            {/* Header space (match print: 64px + 2cm) */}
                            <div
                                className="shrink-0"
                                style={{ height: 'calc(4rem + 2cm)' }}
                                aria-label="Header space"
                            />

                            {/* OPD card header block */}
                            <div className="border-t border-b border-gray-300 py-3">
                                <div className="grid grid-cols-2 gap-6 text-sm">
                                    <div className="space-y-1">
                                        <p><span className="text-gray-600">UHID No.</span> <span className="font-mono">{formatPatientId(patient)}</span></p>
                                        <p><span className="text-gray-600">Patient Name</span> {patient?.name ?? '—'}</p>
                                        <p><span className="text-gray-600">Age / Sex</span> {getAge(patient?.dob)} / {formatSex(patient?.gender)}</p>
                                        <p><span className="text-gray-600">Consultant</span> Dr {(doctor as any)?.name ?? '—'}</p>
                                        <p><span className="text-gray-600">Specialisation</span> {(doctor as any)?.specialisation ?? '—'}</p>
                                        <p><span className="text-gray-600">Provisional Diagnosis</span> __________________________</p>
                                    </div>
                                    <div className="space-y-1 text-right">
                                        <p className="text-center font-bold tracking-wide">OPD Card</p>
                                        <p>
                                          <span className="text-gray-600">Seq.No</span>{' '}
                                          <span className="font-mono">{typeof sequenceNo === 'number' && sequenceNo > 0 ? sequenceNo : '__________'}</span>
                                        </p>
                                        <p><span className="text-gray-600">Visit Date</span> <span className="font-mono">{formatDateTimeNumeric(visitAt)}</span></p>
                                        <p><span className="text-gray-600">Mobile No.</span> <span className="font-mono">{maskMobile((patient as any)?.phone ?? (patient as any)?.phoneNumber)}</span></p>
                                        <p><span className="text-gray-600">Valid Up to</span> <span className="font-mono">{formatDate(validUpto)}</span></p>
                                    </div>
                                </div>
                            </div>

                            {/* Sections */}
                            <div className="py-3 space-y-4 text-sm">
                                <div className="flex gap-6 items-start">
                                    <div className="shrink-0 w-[360px]">
                                        <div className="font-medium text-gray-700 text-[11px]">Past History</div>
                                        <div className="grid grid-cols-2 gap-y-1 gap-x-3 mt-1 text-[10px] max-w-[360px]">
                                            {[
                                                'Previous Major Hospitalization',
                                                'Previous drug History',
                                                'Surgery',
                                                'Allergies',
                                                'Thyroid',
                                                'Epilepsy',
                                                'Hypertension',
                                                'Diabetes',
                                            ].map((t) => (
                                                <div key={t} className="flex items-center gap-2">
                                                    <span className="inline-block h-3 w-3 border border-gray-800" />
                                                    <span>{t}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-gray-700">VITALS</div>
                                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-xs">
                                            <span><span className="text-gray-600">Temp</span> ____</span>
                                            <span><span className="text-gray-600">BP</span> ____</span>
                                            <span><span className="text-gray-600">R/R</span> ____</span>
                                            <span><span className="text-gray-600">Pulse</span> ____</span>
                                            <span><span className="text-gray-600">SpO2</span> ____</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="font-medium text-gray-700">Chief Complaints</div>
                                    <div className="h-24 mt-2" />
                                </div>

                                <div>
                                    <div className="font-medium text-gray-700">Treatment / Prescription</div>
                                    <div className="h-28 mt-2" />
                                </div>

                                <div>
                                    <div className="font-medium text-gray-700">Investigation Adviced</div>
                                    <div className="h-24 mt-2" />
                                </div>

                                <div>
                                    <div className="font-medium text-gray-700">Follow up</div>
                                    <div className="mt-2">________________________________________</div>
                                </div>
                            </div>

                            {/* Footer space */}
                            <div className="h-14" aria-label="Footer space" />
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handlePrint} disabled={!appointment} className="gap-2">
                        <Printer className="w-4 h-4" />
                        Print
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
